import React, { useMemo, useState } from "react";
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Menu, MenuItem } from "@mui/material";
import { useDispatch } from "react-redux";
import { setFilteredLessonIds } from "../redux/filtersSlice";
import { useNavigate } from "react-router-dom";

const LessonPaymentsSummary = ({ lessons, transactions }) => {
  const navigate = useNavigate();

  const summaries = useMemo(() => {
    if (!Array.isArray(lessons) || !Array.isArray(transactions)) return {};

    const futureDebtLessonsMap = {};
    const pastDebtLessonsMap = {};
    const lessonsDebtsByCurrency = {};

    const currencies = Array.from(
      new Set([
        ...transactions.map(tx => tx.ccy),
        ...lessons.map(l => l.ccy)
      ])
    );

    const calcSummaryForCurrency = (ccy) => {
      const txsByCcy = transactions.filter(tx => tx.ccy === ccy);

      const depositTxsByCcy = txsByCcy.filter(tx => tx.type === "deposit")
      const depositsByCcy = depositTxsByCcy.reduce((sum, tx) => sum + tx.amount, 0);
      
      const paymentTxsByCcy = txsByCcy.filter(tx => tx.type === "payment");
      const paymentsByCcy = paymentTxsByCcy.reduce((sum, tx) => sum + tx.amount, 0);

      const netDepositByCcy = depositsByCcy - paymentsByCcy;

      const pastLessonsByCcy = lessons.filter(
        l => l.ccy === ccy &&
        ["finished", "billed"].includes(l.status)
      );

      const futureLessonsByCcy = lessons.filter(
        l => l.ccy === ccy &&
        ["confirmed", "submitted"].includes(l.status)
      );

      const calcLessonBalances = (lessonList) => {
        return lessonList.map(lesson => {
          const lessonPaymentTxs = paymentTxsByCcy.filter(tx => tx.lesson?.uid === lesson.uid);
          const paid = lessonPaymentTxs.reduce((s, tx) => s + tx.amount, 0);
          return {
            lesson,
            balance: lesson.fee - paid,
            txs: lessonPaymentTxs
          }
        });
      }

      const pastLessonBalances = calcLessonBalances(pastLessonsByCcy);
      const futureLessonBalances = calcLessonBalances(futureLessonsByCcy);

      const pastDebtLessons = pastLessonBalances.filter(lb => lb.balance > 0).map(lb => lb.lesson);
      const futureDebtLessons = futureLessonBalances.filter(lb => lb.balance > 0).map(lb => lb.lesson);

      const calcLessonDebts = (lessonBalances) => {
        return lessonBalances.reduce((sum, lb) => {
          return sum + lb.balance;
        }, 0);
      }

      const pastDebt = calcLessonDebts(pastLessonBalances);
      const futureDebt = calcLessonDebts(futureLessonBalances);
      const overallDebt = pastDebt + futureDebt;

      if (pastLessonsByCcy.length) pastDebtLessonsMap[ccy] = pastLessonsByCcy.map(l => l.uid);
      if (futureLessonsByCcy.length) futureDebtLessonsMap[ccy] = futureLessonsByCcy.map(l => l.uid);
      lessonsDebtsByCurrency[ccy] = {
        past: pastLessonsByCcy,
        future: futureLessonsByCcy,
        overall: [...pastLessonsByCcy, ...futureLessonsByCcy],
      };

      return {
        summary: { deposit: netDepositByCcy, pastDebt, futureDebt, overallDebt },
        // labels:  { deposit: "Deposit", pastDebt: "Past Debt", futureDebt: "Future Debt", overallDebt: "Overall Debt" },
        lessons: { pastDebt: pastDebtLessons, futureDebt: futureDebtLessons, overallDebt: [...pastDebtLessons, ...futureDebtLessons]},
        txs:     { deposit: depositTxsByCcy }
     };
    };

    const result = {};
    currencies.forEach(ccy => {
      result[ccy] = calcSummaryForCurrency(ccy);
    });

    return result;
  }, [lessons, transactions]);

  const format = (n) => n?.toFixed(2);

  const getColorIndication = (key, value, map) => {
    if (key === 'deposit') {
      return value - map.summary.futureDebt - map.summary.pastDebt >= 0 ? 'success.main' : 'error.main';
    } else if (key === 'futureDebt') {
      return value > 0 ? value < map.summary.deposit ? "warning.main" : "error.main" : "success.main";
    } else if (key === 'pastDebt') {
      return value > 0 ? "error.main" : "success.main";
    } else {
      return value > 0 ? "warning.main" : "success.main";
    }
  }

  const dispatch = useDispatch();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuLessons, setMenuLessons] = useState([]);

  const handleCellClick = (event, key, ccy) => {
    const lessonsToSet = summaries[ccy].lessons[key];
    if (!lessonsToSet) return;

    setMenuAnchor(event.currentTarget);
    setMenuLessons(lessonsToSet);
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleViewLessons = () => {
    dispatch(setFilteredLessonIds(menuLessons.map(l => l.uid)));
    handleCloseMenu();
    navigate('/billing');
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Payment Summary</Typography>

      {Object.keys(summaries).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No payment data available.
        </Typography>
      ) : (
        <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {Object.keys(summaries).map(ccy => (
                <TableCell key={ccy} align="right"><strong>{ccy}</strong></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { key: "deposit", label: "Deposit" },
              { key: "futureDebt", label: "Future Debt" },
              { key: "pastDebt", label: "Past Debt" },
              { key: "overallDebt", label: "Overall Debt" },
            ].map(({ key, label }) => (
              <TableRow key={key}>
                <TableCell><strong>{label}</strong></TableCell>
                {Object.entries(summaries).map(([ccy, s]) => {
                  const value = s.summary[key];
                  const color = getColorIndication(key, value, s);
                  return (
                    <TableCell
                      key={ccy}
                      align="right"
                      sx={{
                        color,
                        fontWeight: 500,
                        border: "1px solid rgba(224,224,224,1)",
                        cursor: key.includes("Debt") ? "pointer" : "default"
                      }}
                      onClick={(e) => handleCellClick(e, key, ccy)}
                    >
                      {format(value)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleViewLessons}>
          View Lessons
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default LessonPaymentsSummary;
